import React, { HTMLAttributes, useState } from 'react'
import { ArrowLeft, X } from 'react-feather'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@frame/ui-primitives/button'
import { H5 } from '@frame/ui-primitives/h5'

import { useRootContext } from '@frame/lib/context'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { isSessionInTerminalState } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { ExitPrompt, ExitPromptType } from '../exit-prompt'
import { HeaderLogo } from './header-logo'

type Props = {
  backButton?: {
    visible?: boolean
    onClick?: () => void
  }
  cancelButton?: {
    visible?: boolean
    onClick?: () => void
    forceOnClick?: boolean
  }
  showCobranding?: boolean
  className?: HTMLAttributes<HTMLElement>['className']
  title?: string
  disabled?: boolean
  showExitPrompt?: boolean
  exitPromptTitle?: string
  exitPromptType?: ExitPromptType
  pathname?: string
  handleBack?: () => void
  handleCancel?: () => void
  'data-cy'?: string
}

export function WidgetHeaderWithNavigation(props: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  const { events } = useRootContext()
  const consumerInitiated = useStore((state) => state.session?.consumer_initiated)
  const [showPrompt, setShowPrompt] = useState(false)

  function handleCancel() {
    if (!consumerInitiated && !props.cancelButton?.forceOnClick) {
      navigate(routes.confirmExit)
    } else if (props.showExitPrompt) {
      setShowPrompt(true)
    } else if (props.cancelButton?.onClick) {
      props.cancelButton?.onClick()
    } else {
      events.emit('close')
    }
  }

  function handleBack() {
    if (props.backButton?.onClick) {
      props.backButton?.onClick()
    } else {
      navigate(-1)
    }
  }

  return (
    <>
      <BaseWidgetHeader
        data-cy={DataAttribute.HEADER}
        handleBack={handleBack}
        handleCancel={handleCancel}
        backButton={{ ...props.backButton, visible: true }}
        cancelButton={{ ...props.cancelButton, visible: true }}
        pathname={location.pathname}
        {...props}
      />
      {renderExitPrompt(props, showPrompt, setShowPrompt)}
    </>
  )
}

export function BaseWidgetHeader({ showCobranding = false, ...props }: Props) {
  const { events } = useRootContext()
  const { state, consumerInitiated, cobrandingLogoUrl } = useStore((state) => ({
    state: state.session?.state,
    consumerInitiated: Boolean(state.session?.consumer_initiated),
    cobrandingLogoUrl: state.session?.configuration.cobranding?.logo_url,
  }))

  const hideCancelButton = !consumerInitiated && isSessionInTerminalState(state)

  function defaultCancel() {
    events.emit('close')
  }

  const _backButton = { visible: props.backButton?.visible ?? Boolean(props.handleBack), onClick: props.handleBack }
  const _cancelButton = {
    visible: props.cancelButton?.visible ?? !hideCancelButton,
    onClick: props.handleCancel ?? defaultCancel,
  }
  const shouldShowTitle = Boolean(props.title)
  const shouldShowCobranding = showCobranding && !shouldShowTitle

  if (shouldShowTitle) {
    return <HeaderWithTitle {...props} backButton={_backButton} cancelButton={_cancelButton} />
  }

  if (shouldShowCobranding) {
    return (
      <HeaderWithLogo
        {...props}
        backButton={_backButton}
        cancelButton={_cancelButton}
        cobrandingLogoUrl={cobrandingLogoUrl}
      />
    )
  }

  return (
    <header
      data-cy={props['data-cy'] ?? DataAttribute.HEADER_NO_NAVIGATION}
      className={cn(
        'flex min-h-[72px] items-center justify-between p-5',
        !_backButton.visible && 'justify-end',
        props.className
      )}
    >
      {_backButton.visible && (
        <div className="flex items-center">
          <Button
            aria-label="Back"
            data-cy={DataAttribute.HEADER_BTN_BACK}
            onClick={_backButton.onClick}
            Icon={ArrowLeft}
            border={false}
            intent="secondary"
            disabled={props.disabled}
            className="mr-3"
          />
        </div>
      )}
      {showCobranding && <HeaderLogo cobrandingLogoUrl={cobrandingLogoUrl} />}
      {_cancelButton.visible && (
        <Button
          aria-label="Close"
          data-cy={DataAttribute.HEADER_BTN_CANCEL}
          onClick={_cancelButton.onClick}
          Icon={X}
          border={false}
          intent="secondary"
          disabled={props.disabled}
        />
      )}
    </header>
  )
}

function HeaderWithTitle({ backButton, cancelButton, title, disabled, className, ...props }: Props) {
  return (
    <header
      data-cy={props['data-cy'] ?? DataAttribute.HEADER_NO_NAVIGATION}
      className={cn('flex min-h-[72px] items-center justify-between p-5', className)}
    >
      {backButton?.visible ? (
        <div className="flex items-center">
          <Button
            aria-label="Back"
            data-cy={DataAttribute.HEADER_BTN_BACK}
            onClick={backButton?.onClick}
            Icon={ArrowLeft}
            border={false}
            intent="secondary"
            disabled={disabled}
            className="mr-2"
          />
          <div>
            <H5 as="div" className="text-sm font-medium text-moss-500">
              {title}
            </H5>
          </div>
        </div>
      ) : (
        <div>
          <H5 as="div" className="text-sm font-medium text-moss-500">
            {title}
          </H5>
        </div>
      )}
      <div>
        {cancelButton?.visible && (
          <Button
            aria-label="Close"
            data-cy={DataAttribute.HEADER_BTN_CANCEL}
            onClick={cancelButton.onClick}
            Icon={X}
            border={false}
            intent="secondary"
            disabled={disabled}
          />
        )}
      </div>
    </header>
  )
}

function HeaderWithLogo({
  backButton,
  cancelButton,
  disabled,
  cobrandingLogoUrl,
  className,
  ...props
}: Props & { cobrandingLogoUrl?: string | null }) {
  return (
    <header
      data-cy={props['data-cy'] ?? DataAttribute.HEADER_NO_NAVIGATION}
      className={cn('flex min-h-[72px] items-center justify-between p-5', className)}
    >
      <div className={cn('flex flex-1 items-center', cobrandingLogoUrl && !backButton?.visible && 'hidden')}>
        {backButton?.visible && (
          <Button
            aria-label="Back"
            data-cy={DataAttribute.HEADER_BTN_BACK}
            onClick={backButton?.onClick}
            Icon={ArrowLeft}
            border={false}
            intent="secondary"
            disabled={disabled}
            className="mr-3"
          />
        )}
      </div>
      <HeaderLogo cobrandingLogoUrl={cobrandingLogoUrl} className={cn(!cobrandingLogoUrl && 'mx-auto')} />
      <div className="flex flex-1 justify-end">
        {cancelButton?.visible && (
          <Button
            aria-label="Close"
            data-cy={DataAttribute.HEADER_BTN_CANCEL}
            onClick={cancelButton?.onClick}
            Icon={X}
            border={false}
            intent="secondary"
            disabled={disabled}
          />
        )}
      </div>
    </header>
  )
}

function renderExitPrompt(
  props: Props,
  showPrompt: boolean,
  setShowPrompt: React.Dispatch<React.SetStateAction<boolean>>
) {
  return (
    props.showExitPrompt && (
      <ExitPrompt
        show={showPrompt}
        hide={() => setShowPrompt(false)}
        title={props.exitPromptTitle}
        type={props.exitPromptType}
      />
    )
  )
}
